/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getAllSolutions } from "redux/memoizedSelectors";
import DataLoader from "shared/components/data-loader";
import { Spinner } from "shared/components/spinners/Spinner";
import { SideModal } from "shared/cx-components/cx-components.style";
import DetailPanel from "shared/cx-components/detail-panel/detail-panel";
import Header from "shared/cx-components/header";
import HeaderTitle from "shared/cx-components/header-title";
import Relations from "shared/cx-components/relations/relations";
import { TypesTabs } from "shared/cx-components/relations/tabs-data";
import { ItemProvider, useItem } from "shared/dynamic-imports";
import { useNavigationListData } from "shared/hooks";
import { EntityList } from "utils/optionsSets/OptionSets";
import { PermissionManager } from "utils/permissionManager";
import BaseActions from "../actions/BaseActions";
import { getSolutionRelatedRecords, getSolutionRelatedRecordsFn, updateSolution } from "../reducer/SolutionsActions";

type Props = {
	isOpen: boolean;
	onClose: () => void;
	customItemsIds?: string[];
	id: string;
	stageId?: string;
	swimlaneKey?: string;
};

export default function EditSolution(props: Props): JSX.Element {
	const { isOpen, onClose, id } = props;
	const currentItem = useSelector(({ solutions }: any) => solutions?.all_solutions?.find((solution: any) => solution.id === id));
	useEffect(() => {
		return () => {
			if (id) {
				getSolutionRelatedRecordsFn({ solutionId: id });
			}
		};
	}, []);

	return (
		<SideModal show={isOpen} onHide={onClose} scroll backdrop placement="end" container={document.getElementById("modal-root")}>
			<div>
				<DataLoader asyncFns={[{ asyncFn: getSolutionRelatedRecords, parameter: { solutionId: id } }]}>
					<ItemProvider initialAction={currentItem} updateService={updateSolution}>
						<Content {...props} />
					</ItemProvider>
				</DataLoader>
			</div>
		</SideModal>
	);
}
const Content = (props: Props) => {
	const { onClose, customItemsIds, stageId } = props;
	const { currentItem, setCurrentItem } = useItem();

	const [loading, setLoading] = useState(false);

	const updateData = (id: string) => {
		setLoading(true);
		getSolutionRelatedRecordsFn({
			solutionId: id,
			onSuccess: (data: any) => {
				setCurrentItem(data);
				setLoading(false);
			},
			onFailed: () => setLoading(false),
		});
	};

	const { navigationList, updateCurrentItem } = useNavigationListData({ allItemsSelector: getAllSolutions, getSelectedItemsIds: getTablePath, selectedItemsIds: customItemsIds, update: updateData });
	const isReadOnly = useSelector(({ auth }: any) => auth.userInfo.UserType)?.isReadOnly();
	const userAccess: any = useSelector(({ maps }: any) => maps?.currentUser?.roleName);
	const { editAllowed }: any = PermissionManager({ userAccess }) || {};
	const disabled = isReadOnly || !editAllowed;

	const actions = BaseActions({ solution: currentItem, onClose });
	return (
		<>
			{loading ? (
				<Spinner isFixed={false} />
			) : (
				<>
					<Header
						onClose={onClose}
						actions={actions}
						navigationList={navigationList as string[]}
						currentItemId={currentItem?.id}
						setCurrentItem={updateCurrentItem}
						entityName="cem_cxsolution"
						disabled={disabled}
						showCopyUrl={!stageId}
					/>
					<HeaderTitle disabled={disabled} icon="SOLUTION" labelPlaceholder="NEW_SOLUTION" />
					<DetailPanel disabled={disabled} stageId={stageId} recordType="cem_cxsolution" />
					<Relations disabled={disabled} stageId={stageId} entityType={TypesTabs.Solutions} onClose={onClose} />
				</>
			)}
		</>
	);
};

const getTablePath = (pathname: string) => {
	switch (pathname) {
		case "/EditCxProgram":
			return ({ ui }: any) => ui.table?.entity?.[EntityList.Solution]?.cxProgram?.filteredIds;
		default:
			return ({ ui }: any) => ui.table?.entity?.[EntityList.Solution]?.solutionsList?.filteredIds;
	}
};
